import {Injectable, Injector} from '@angular/core';
import {ApiResponse, BaseApiService} from '../base-api.service';
import {ApiStatusEnum} from '../../../Shared/Enum/api-status.enum';
import {DocumentsReviewModel} from '../../Models/Documents-review/documents-review-model';
import {DocumentsReviewFilters} from '../../../Models/Documents-review/documents-review-filters.model';
import {DeliveryNotesReviewFilters} from '../../../Models/Documents-review/deliver-note-review-filters.model';
import {ExpensesReviewFilters} from '../../../Models/Documents-review/expenses-review-filters.model';
import {ExpenseReviewStore} from '../../../Models/Documents-review/expense-review-store.model';
import {PayrollReviewStore} from '../../../Models/Documents-review/payroll-review-store.model';
import {UnclassifiedReviewStore} from '../../../Models/Documents-review/unclassified-review-store.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentsReviewApiService extends BaseApiService {

  constructor(
    protected override injector: Injector,
    public model: DocumentsReviewModel,
  ) {
    super(injector);
  }


  /**
   * Obtener los documentos por revisar
   *
   */
  public async getDocumentsReview(filters: DocumentsReviewFilters | null, save: boolean = true): Promise<ApiResponse> {
    const storeId: number = filters?.store_id || 0;

    const [deliveryNotesRes, personalExpensesRes, generalExpensesRes, unclassifiedRes] = await Promise.allSettled([
      this.getDeliveryNotesReview(DeliveryNotesReviewFilters.forReview(storeId)),
      this.getPersonalExpensesSimples(ExpensesReviewFilters.forReview(storeId)),
      this.getGeneralExpensesSimples(ExpensesReviewFilters.forReview(storeId)),
      this.getUnclassifiedDocuments(filters),
    ]);

    const getDataOrDefault = (result: PromiseSettledResult<ApiResponse>): any[] =>
      result.status === 'fulfilled' ? result.value?.data || [] : [];

    const combinedResponse: ApiResponse = {
      data: {
        delivery_notes: getDataOrDefault(deliveryNotesRes),
        expenses: getDataOrDefault(generalExpensesRes),
        payrolls: getDataOrDefault(personalExpensesRes),
        unclassified: getDataOrDefault(unclassifiedRes),
      },
      message: '',
      status: ApiStatusEnum.success,
    };

    if (save) {
      await this.model.save(combinedResponse.data);
    }

    return combinedResponse;
  }


  /**
   * Obtener los albaranes que están por revisar
   *
   */
  public getDeliveryNotesReview(params: DeliveryNotesReviewFilters): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('purchases/delivery-notes', params, 'get').subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Obtener los gastos personales simples
   *
   */
  public getPersonalExpensesSimples(filters: ExpensesReviewFilters): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('getGastosPersonalesSimples', filters).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Obtener el detalle de un gasto personal
   *
   */
  public getPersonalExpense(params: {id: number}): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall(`personal-expenses/${params.id}`, {}, 'get').subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Guardar gasto personal
   *
   */
  public setPersonalExpense(params: PayrollReviewStore): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('setGastoPersonalSimple', params).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Eliminar un gasto personal
   *
   */
  public deletePersonalExpense(params: { id: number }): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('deleteGastoSimple', params).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Obtener los gastos generales simples
   *
   */
  public getGeneralExpensesSimples(params: ExpensesReviewFilters): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('getGastosGeneralesSimples', params).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Obtener el detalle de un gasto general
   *
   */
  public getGeneralExpense(params: { id: number }): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall(`expenses/${params.id}`, {}, 'get').subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Comprobar la referencia del gasto
   *
   */
  public checkExpenseReference(params: { supplier_id: number, external_reference: string, expense_id?: number }): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('expenses/check-duplicate-invoice', params).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Guardar gasto general
   *
   */
  public setGeneralExpense(params: ExpenseReviewStore): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('setGastoGeneralSimple', params).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Eliminar un gasto general
   *
   */
  public deleteGeneralExpense(params: { id: number }): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('deleteGastoSimple', params).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Obtener documentos sin clasificar
   *
   */
  public getUnclassifiedDocuments(filters: DocumentsReviewFilters | null): Promise<ApiResponse> {
    const params = {...filters, failed: true, pending: true};

    return new Promise((resolve, reject) => {

      this.httpCall('ocr/scans', params, 'get').subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });
  }


  /**
   * Eliminar documento sin clasificar
   *
   */
  public deleteUnclassifiedDocument(params: { id: number }): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      this.httpCall(`ocr/scans/${params.id}`, {'_method': 'delete'}, 'post').subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Inicia el proceso de un scan sin clasificar con los datos proporcionados por el usuario
   *
   */
  public processUnclassifiedScan(params: UnclassifiedReviewStore): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall(`ocr/scans/${params.id}/process`, params, 'post').subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }


  /**
   * Subir documentos escaneados
   *
   */
  public uploadScanDocuments(params: FormData): Promise<ApiResponse> {

    return new Promise((resolve, reject) => {

      return this.httpCall('ocr/scan-requests', params).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err: Error) => {
          reject(err);
        }
      });

    });

  }

}
